import React from 'react';
import cx from 'classnames';
import ReactMarkdown, {Components} from 'react-markdown';
import {Title, Text} from '@priority-software/priority-style-react';
import styles from './style.module.scss';

type MarkdownRendererProps = {
    content: string;
    componentClasses?: string;
};

type TextProps = {
    children: string | React.ReactNode;
    fontWeight?: 'normal' | 'bold' | 'medium' | 'thin';
    fontStyle?: 'normal' | 'italic';
};

type Bullet = {
    props: {
        children: string;
    };
    key: string;
};

enum ListType {
    Ul = 'ul',
    Ol = 'ol'
}

const renderTitle = (isMain: boolean, text) => {
    const titleTag = isMain ? 'h2' : 'h3';
    return (
        <Title
            skin={titleTag}
            text={text}
            textColor="primary-500"
            componentClasses={isMain ? styles.mainTitle : styles.subTitle}
        />
    );
};

const renderText = ({children, fontWeight, fontStyle}: TextProps) => {
    const tag = fontWeight || fontStyle ? 'span' : 'p';
    return (
        <Text
            tag={tag}
            skin="paragraph-1"
            textColor="primary-700"
            fontWeight={fontWeight}
            fontStyle={fontStyle}
            componentClasses={styles.standardText}
        >
            {children}
        </Text>
    );
};

const renderBullets = (bullets: Bullet[], listType: ListType) => {
    return bullets.map((bullet: Bullet, idx: number) => {
        if (React.isValidElement(bullet) && bullet.props?.children) {
            return (
                <li key={bullet.key} className={styles.listItem}>
                    <Text
                        tag="span"
                        skin="paragraph-1"
                        textColor="primary-700"
                        componentClasses={styles.bullet}
                    >
                        {listType === ListType.Ul ? '•' : idx + 1 + '.'}
                    </Text>
                    <Text
                        tag="span"
                        skin="paragraph-1"
                        textColor="primary-700"
                        componentClasses={styles.listItemContent}
                    >
                        {bullet.props.children}
                    </Text>
                </li>
            );
        }
        return null;
    });
};

const renderUnorderedList = children => {
    const bullets = children.filter(child => typeof child !== 'string');
    return <ul className={styles.unorderedList}>{renderBullets(bullets, ListType.Ul)}</ul>;
};

const renderOrderedList = children => {
    const bullets = children.filter(child => typeof child !== 'string');
    return <ol className={styles.orderedList}>{renderBullets(bullets, ListType.Ol)}</ol>;
};

export const MarkdownRenderer = ({content, componentClasses}: MarkdownRendererProps) => {
    const components = {
        h1: ({node, ...props}) => renderTitle(true, props.children),
        h2: ({node, ...props}) => renderTitle(true, props.children),
        h3: ({node, ...props}) => renderTitle(false, props.children),
        h4: ({node, ...props}) => renderTitle(false, props.children),
        h5: ({node, ...props}) => renderTitle(false, props.children),
        h6: ({node, ...props}) => renderTitle(false, props.children),
        p: ({node, ...props}) => renderText({children: props.children}),
        strong: ({node, ...props}) => renderText({children: props.children, fontWeight: 'bold'}),
        ul: ({node, ...props}) => renderUnorderedList(props.children),
        ol: ({node, ...props}) => renderOrderedList(props.children),
        a: ({node, ...props}) => <a target="_blank" rel="noopener noreferrer" {...props} />
    };

    return (
        <div className={cx(styles.markdownComponent, componentClasses)}>
            <ReactMarkdown components={components as Components}>{content}</ReactMarkdown>
        </div>
    );
};
